import { reducerWithInitialState } from 'typescript-fsa-reducers'
import { LanguageCode, Manifest } from 'mytours-read-api-typescript'

import * as actions from './actions'
import * as apiActions from '../api/actions'
import { AppInfo, Language } from './types'
import { AppMetaData as AppMetaDataNew } from '../api/types'
import { updateManifestMap, createEmptyManifest } from './functions'

export const DEFAULT_APP_INFO: AppInfo = {
	appId: 'DEFAULT',
	projectId: 'DEFAULT',
	appApiKey: 'DEFAULT',
}

export interface ProcessingManifestState {
	state: 'initialising' | 'failed' | 'processing' | 'finished'
	screen: Map<string, actions.ProcessingManifestItem> // key is [itemId + language]
	collection: Map<string, actions.ProcessingManifestItem>
	collectionItem: Map<string, actions.ProcessingManifestItem>
	media: Map<string, actions.ProcessingManifestItem>
	allLanguages: LanguageCode[]
	completedLanguages: LanguageCode[]
}

export interface StoreState {
	readonly appInfo: AppInfo
	readonly loadingMetaData: boolean
	readonly appMetaData?: AppMetaDataNew
	readonly lastUpdated?: number
	readonly selectedLanguage?: Language
	// set to true once the user has set the initial rental app content (only used for rental flavor)
	readonly hasSetRentalAppInfo: boolean
	readonly locationEventsEnabled: boolean
	readonly rentalTokenAppInfo?: AppInfo
	readonly rentalTokenError?: Error
	// when processing an app manifest we store loading state of each section here
	readonly processingManifest?: ProcessingManifestState
	readonly screenTimeout?: number

	readonly appManifest?: Manifest
}

const INITIAL_STATE: StoreState = {
	appInfo: DEFAULT_APP_INFO,
	loadingMetaData: false,
	hasSetRentalAppInfo: false,
	locationEventsEnabled: true,
	appManifest: {},
}

export const reducer = reducerWithInitialState(INITIAL_STATE)
	.case(actions.changeAppInfo, (state, payload) => {
		const appInfo: AppInfo = { appId: payload.id, projectId: payload.projectId, appApiKey: payload.token }
		return {
			...state,
			appInfo,
		}
	})

	// meta data was loaded from disk
	.case(actions.loadedMetaDataComplete, (state, payload) => {
		// if 1993 do not store metaData on redux because this is the default metaData for kiosk flavor
		if (payload.id === 1993) {
			return { ...state }
		}
		return {
			...state,
			appMetaData: payload,
			loadingMetaData: false,
		}
	})

	// fetching meta data from network
	.case(apiActions.fetchAppMetaData.started, state => ({
		// started
		...state,
		loadingMetaData: true,
	}))
	.case(apiActions.fetchAppMetaData.done, state => {
		// success
		const now = Date.now()
		return {
			...state,
			/* DON'T SET THE META DATA ON REDUX YET, instead set the meta data using the loadedMetaDataComplete action.
			   This is so we can build the EStyleSheet from the meta data before the app is recreated. */
			// appMetaData: payload.result,
			// loadingMetaData: false,
			lastUpdated: now,
		}
	})
	.case(apiActions.fetchAppMetaData.failed, state => ({
		// failed
		...state,
		loadingMetaData: false,
	}))

	// APP LANGUAGE SELECTED
	.case(actions.appLanguageSelected, (state, payload): StoreState => ({
		...state,
		selectedLanguage: payload,
	}))

	// SCREEN TIMEOUT SELECTED
	.case(actions.screenTimeoutSelected, (state, payload): StoreState => ({
		...state,
		screenTimeout: payload,
	}))

	// RENTAL MODE
	.case(actions.setInitialisedRentalMode, (state, payload): StoreState => {
		if (payload) {
			return {
				...state,
				hasSetRentalAppInfo: payload,
			}
		} else {
			// reset all cached content
			return INITIAL_STATE
		}
	})

	// ENABLE LOCATION EVENTS
	.case(actions.enableLocationEvents, (state, payload): StoreState => ({
		...state,
		locationEventsEnabled: payload,
	}))

	// manual rental token entry
	.cases([apiActions.validateRentalToken.started, actions.clearRentalToken], state => ({
		...state,
		rentalTokenError: undefined,
		rentalTokenAppInfo: undefined,
	}))
	.case(apiActions.validateRentalToken.done, (state, payload) => {
		// Don't need to worry about `projectId` since `validateRentalToken` is only used for native
		// convert type
		const rentalTokenAppInfo: AppInfo = {
			appId: String(payload.result.app_id!),
			projectId: '',
			appApiKey: payload.result.token!,
		}
		return { ...state, rentalTokenAppInfo }
	})
	.case(apiActions.validateRentalToken.failed, (state, payload) => ({
		...state,
		rentalTokenError: payload.error,
	}))

	// manifest
	.case(apiActions.fetchManifest.started, state => {
		const manifest: ProcessingManifestState = createEmptyManifest('initialising')
		return {
			...state,
			processingManifest: manifest,
		}
	})
	.case(apiActions.fetchManifestComplete, (state, payload) => ({
		...state,
		appManifest: payload,
	}))
	.case(apiActions.fetchManifest.done, (state, payload) => {
		const { result } = payload
		return {
			...state,
			appManifest: result,
		}
	})
	.case(apiActions.fetchManifest.failed, state => {
		const manifest: ProcessingManifestState = createEmptyManifest('failed')
		return {
			...state,
			processingManifest: manifest,
		}
	})
	.case(actions.updateProcessingManifestState, (state, payload) => {
		if (payload.event === 'processing') {
			// create new manifest (and in processing state)
			const manifest: ProcessingManifestState = createEmptyManifest('processing')
			// set items on each manifest section
			payload.items!.forEach(item => manifest[item.type].set(item.itemId + item.language, item))
			// set all languages
			manifest.allLanguages = payload.allLanguages!
			return {
				...state,
				processingManifest: manifest,
			}
		} else if (payload.event === 'finished' && state.processingManifest) {
			// set finished flag so can display message in UI
			const manifest: ProcessingManifestState = { ...state.processingManifest, state: 'finished' }
			return {
				...state,
				processingManifest: manifest,
			}
		} else if (payload.event === 'languageComplete' && state.processingManifest) {
			const manifest: ProcessingManifestState = { ...state.processingManifest }
			// add completed language to array
			manifest.completedLanguages.push(payload.completedLanguage!)
			return {
				...state,
				processingManifest: manifest,
			}
		} else if (payload.event === 'clear') {
			// clear the manifest details
			return {
				...state,
				processingManifest: undefined,
			}
		}
		// return state unchanged
		return state
	})
	// manifest item
	.case(actions.updateProcessingManifestStateItem, (state, payload): StoreState => {
		if (state.processingManifest) {
			const { itemId, language, type, state: itemState } = payload
			const newManifest: ProcessingManifestState = updateManifestMap(
				state.processingManifest,
				itemId,
				language,
				type,
				itemState,
			)
			return { ...state, processingManifest: newManifest }
		}
		return state
	})
